import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Card,
  CardContent,
  // Button,
  IconButton,
  Tooltip,
  // Stack,
  // useMediaQuery
  Menu,
  MenuItem
  // Divider
} from '@mui/material';
import { CheckCircleOutlined, CloseCircleOutlined, RocketOutlined, HeartOutlined, ExportOutlined } from '@ant-design/icons';
import DownloadIcon from '@mui/icons-material/Download';
import NewOverviewSection from 'sections/lp-analysis/NewOverviewSection';
import MarketTrends from 'sections/lp-analysis/MarketTrends';
import USPSection from 'sections/lp-analysis/USPSection';
import ReviewsNumbers from 'sections/reviews/ReviewsNumbers';
import captureScreenshot from 'utils/screenshotUtils';
import pptxgen from 'pptxgenjs';

const ensureHttpProtocol = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

const removeProtocol = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '');
};

// Custom Reviews Component that doesn't rely on Redux but matches original layout exactly
const SimpleReviewsSection = ({ reviewsData }) => {
  // Skip rendering if data is missing
  if (!reviewsData || !reviewsData.review_analysis_results) {
    return null;
  }

  const reviewAnalysisResults = reviewsData.review_analysis_results;
  const rating_percentages = reviewsData.rating_percentages;
  const review_sources = reviewsData.review_sources;

  const renderList = (items, icon, color) => {
    // Add safety check to ensure items is an array
    const itemsArray = Array.isArray(items) ? items : [];

    return (
      <List dense>
        {itemsArray.map((item, index) => (
          <ListItem key={index} sx={{ paddingBottom: '0px' }}>
            <ListItemIcon>{React.createElement(icon, { style: { color } })}</ListItemIcon>
            <ListItemText primary={<Typography variant="body1">{item}</Typography>} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderProsAndCons = (data, icon, color) => {
    // Add safety check to ensure data is an object
    if (!data || typeof data !== 'object') return null;

    return Object.keys(data).map((key, index) => (
      <Box key={index} sx={{ marginBottom: '16px' }}>
        <Typography variant="h5" color="primary">
          {key}
        </Typography>
        <List dense>
          {Array.isArray(data[key])
            ? data[key].map((item, subIndex) => (
                <ListItem key={subIndex} sx={{ paddingBottom: '0px' }}>
                  <ListItemIcon>{React.createElement(icon, { style: { color } })}</ListItemIcon>
                  <ListItemText primary={<Typography variant="body1">&#34;{item}&#34;</Typography>} />
                </ListItem>
              ))
            : null}
        </List>
      </Box>
    ));
  };

  return (
    <Grid container spacing={3} sx={{ alignItems: 'center', justifyContent: 'center' }}>
      {/* Service Name */}
      <Grid item xs={12} md={10}>
        <Typography variant="h3" ml={3} align="center">
          Reviews analysis
        </Typography>
      </Grid>

      {/* Rating Percentages and Review Sources - Using the actual ReviewsNumbers component */}
      <ReviewsNumbers rating_percentages={rating_percentages} review_sources={review_sources} />

      {/* Feature Requests */}
      <Grid item xs={12} md={10}>
        <Card sx={{ display: 'flex', marginBottom: '32px' }}>
          <Box sx={{ backgroundColor: '#e3f2fd', width: '8px' }} />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <RocketOutlined style={{ fontSize: '32px', marginRight: '8px' }} />
              <Typography variant="h4" color="primary">
                Most common feature requests
              </Typography>
            </Box>
            <Typography variant="h6" color="textSecondary" sx={{ marginBottom: '16px' }}>
              Discover the most requested features from users. Gain a clear understanding of what enhancements people desire the most.
            </Typography>
            {reviewAnalysisResults.feature_requests && reviewAnalysisResults.feature_requests.length > 0 ? (
              renderList(reviewAnalysisResults.feature_requests, CheckCircleOutlined, 'blue')
            ) : (
              <Typography>No feature requests available</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Pros */}
      <Grid item xs={12} md={10}>
        <Card sx={{ display: 'flex', marginBottom: '32px' }}>
          <Box sx={{ backgroundColor: '#e8f5e9', width: '8px' }} />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <HeartOutlined style={{ fontSize: '28px', marginRight: '8px' }} />
              <Typography variant="h4" color="primary">
                Most loved features
              </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ marginBottom: '16px' }}>
              Uncover the most cherished aspects. Learn which features users appreciate the most and the reasons behind their preferences.
            </Typography>
            {reviewAnalysisResults.pros ? (
              renderProsAndCons(reviewAnalysisResults.pros, CheckCircleOutlined, 'green')
            ) : (
              <Typography>No Pros available</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Cons */}
      <Grid item xs={12} md={10}>
        <Card sx={{ display: 'flex' }}>
          <Box sx={{ backgroundColor: '#ffebee', width: '8px' }} />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <CloseCircleOutlined style={{ fontSize: '28px', marginRight: '8px', color: 'red' }} />
              <Typography variant="h4" color="primary">
                Cons based on user feedback
              </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ marginBottom: '16px' }}>
              Identify common drawbacks as highlighted by user feedback. Understand the areas that users feel need improvement.
            </Typography>
            {reviewAnalysisResults.cons ? (
              renderProsAndCons(reviewAnalysisResults.cons, CloseCircleOutlined, 'red')
            ) : (
              <Typography>No Cons available</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Generic component for company analysis (usable for both my company and competitors)
const CompanyAnalysis = ({ companyData }) => {
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);

  useEffect(() => {
    if (companyData) {
      setLoading(false);
    }
  }, [companyData]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Export as PNG
  const downloadAsPng = () => {
    const fileName = `${companyData.name || removeProtocol(companyData.website) || 'company'}-analysis`;
    captureScreenshot({
      contentRef,
      filename: fileName,
      afterCapture: handleClose
    });
  };

  // Export as DOC
  const downloadAsDoc = () => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    // Escape RTF characters
    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text
        .replace(/\\/g, '\\\\')
        .replace(/{/g, '\\{')
        .replace(/}/g, '\\}')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/\u2022/g, '•');
    };

    // Format trend object
    const formatTrend = (trend) => {
      if (typeof trend === 'string') return trend;
      return `${trend.title || ''}\n${trend.description || ''}`;
    };

    // Format benchmark object
    const formatBenchmark = (benchmark) => {
      if (typeof benchmark === 'string') return benchmark;
      return `${benchmark.metric_value || ''} ${benchmark.metric_name || ''}\n${benchmark.description || ''}`;
    };

    // Add title
    rtf += '\\b\\fs28 Company Analysis\\b0\\fs20\\par\\par\n';
    rtf += `\\i ${companyData.name || companyData.website}\\i0\\par\\par\n`;

    // Add sections
    if (companyData.analysis_data?.overview_result?.result?.[0]) {
      rtf += '\\b\\fs24 Overview\\b0\\fs20\\par\\par\n';
      const overview = companyData.analysis_data.overview_result.result[0];
      Object.entries(overview).forEach(([key, value]) => {
        rtf += `\\b ${escapeRTF(key)}\\b0\\par\n`;
        rtf += `${escapeRTF(value)}\\par\\par\n`;
      });
    }

    // Add USP section if available
    if (companyData.analysis_data?.usp) {
      rtf += '\\b\\fs24 USP & Distinctions\\b0\\fs20\\par\\par\n';
      Object.entries(companyData.analysis_data.usp).forEach(([key, items]) => {
        if (Array.isArray(items)) {
          rtf += `\\b ${escapeRTF(key)}\\b0\\par\n`;
          items.forEach((item) => {
            rtf += `- ${escapeRTF(item)}\\par\n`;
          });
          rtf += '\\par\n';
        }
      });
    }

    // Add Market Trends section
    if (companyData.analysis_data?.market_trends) {
      rtf += '\\b\\fs24 Market Trends\\b0\\fs20\\par\\par\n';

      // Add Trends
      if (companyData.analysis_data.market_trends.trends) {
        rtf += '\\b Trends\\b0\\par\\par\n';
        companyData.analysis_data.market_trends.trends.forEach((trend) => {
          rtf += `- ${escapeRTF(formatTrend(trend))}\\par\\par\n`;
        });
      }

      // Add Benchmarks
      if (companyData.analysis_data.market_trends.benchmarks) {
        rtf += '\\b Benchmarks\\b0\\par\\par\n';
        Object.entries(companyData.analysis_data.market_trends.benchmarks).forEach(([, value]) => {
          const benchmarkText = formatBenchmark(value);
          rtf += `\\b ${escapeRTF(benchmarkText.split('\n')[0])}\\b0\\par\n`;
          rtf += `${escapeRTF(benchmarkText.split('\n')[1])}\\par\\par\n`;
        });
      }
    }

    rtf += '}';

    const fileName = `${companyData.name || removeProtocol(companyData.website) || 'company'}-analysis.doc`;
    const blob = new Blob([rtf], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  // Export as PPTX
  const downloadAsPPTX = async () => {
    const pptx = new pptxgen();

    // Add title slide
    const titleSlide = pptx.addSlide();
    titleSlide.background = { color: 'FFFFFF' };

    titleSlide.addText('Company Analysis', {
      x: 0.5,
      y: 1,
      w: '90%',
      h: 1,
      fontSize: 32,
      color: '363636',
      align: 'center',
      bold: true
    });

    titleSlide.addText(companyData.name || companyData.website, {
      x: 0.5,
      y: 2,
      w: '90%',
      h: 0.5,
      fontSize: 18,
      color: '666666',
      align: 'center',
      italic: true
    });

    // Add branding
    titleSlide.addText('Powered by', {
      x: 7.0,
      y: 0.5,
      w: 1.1,
      h: 0.4,
      fontSize: 10,
      color: '666666',
      align: 'right'
    });

    titleSlide.addImage({
      path: '/favicon.svg',
      x: 8.15,
      y: 0.51,
      w: 0.3,
      h: 0.3
    });

    titleSlide.addText('Askpot', {
      x: 8.5,
      y: 0.5,
      w: 1,
      h: 0.4,
      fontSize: 13,
      color: '363636',
      bold: true
    });

    // Helper function to estimate text rows - much more aggressive now
    const estimateRowCount = (text) => {
      if (!text) return 1;
      const str = typeof text === 'string' ? text : JSON.stringify(text) || '';
      // Much more aggressive line wrapping - 120 chars per line instead of 80
      const charsPerLine = 120;
      const newlineCount = (str.match(/\n/g) || []).length;
      const lineWrapCount = Math.floor(str.length / charsPerLine);
      // Add 1 for safety, but be more aggressive with the count
      return Math.max(1, newlineCount + Math.ceil(lineWrapCount * 0.8));
    };

    // Helper function to get max rows per slide - reduced by ~20%
    const getMaxRowsPerSlide = (content) => {
      const rowCount = estimateRowCount(content);
      // Reduced by ~20% from previous values
      if (rowCount <= 2) return 8; // Short text - up to 12 items per slide (was 15)
      if (rowCount <= 4) return 6; // Medium text - up to 8 items per slide (was 10)
      return 6; // Long text - up to 6 items per slide (was 8)
    };

    // Add Overview section
    if (companyData.analysis_data?.overview_result?.result?.[0]) {
      const overview = companyData.analysis_data.overview_result.result[0];
      const entries = Object.entries(overview);

      // First, calculate total slides needed
      let totalSlides = 0;
      let currentSlideContent = [];
      let currentSlideRowCount = 0;

      entries.forEach(([key, value]) => {
        const rowCount = estimateRowCount(value);
        const maxRows = getMaxRowsPerSlide(value);

        if (currentSlideRowCount + rowCount > maxRows) {
          totalSlides++;
          currentSlideContent = [[key, value]];
          currentSlideRowCount = rowCount;
        } else {
          currentSlideContent.push([key, value]);
          currentSlideRowCount += rowCount;
        }
      });
      if (currentSlideContent.length > 0) totalSlides++;

      // Now create the slides
      let currentSlide = 0;
      currentSlideContent = [];
      currentSlideRowCount = 0;

      entries.forEach(([key, value]) => {
        const rowCount = estimateRowCount(value);
        const maxRows = getMaxRowsPerSlide(value);

        if (currentSlideRowCount + rowCount > maxRows) {
          // Create new slide with accumulated content
          const slide = pptx.addSlide();
          slide.background = { color: 'FFFFFF' };

          slide.addText('Overview', {
            x: 0.5,
            y: 0.5,
            w: '90%',
            h: 1,
            fontSize: 24,
            color: '363636',
            bold: true
          });

          slide.addText(`Page ${currentSlide + 1}/${totalSlides}`, {
            x: 0.5,
            y: 1.2,
            w: '90%',
            h: 0.5,
            fontSize: 14,
            color: '666666',
            italic: true
          });

          const tableData = [['Field', 'Value'], ...currentSlideContent];
          slide.addTable(tableData, {
            x: 0.5,
            y: 2,
            w: '90%',
            colW: [2.5, 6],
            border: { type: 'solid', color: 'CCCCCC', pt: 1 },
            fontSize: 11,
            color: '363636',
            align: 'left',
            valign: 'middle',
            headerRow: true,
            headerColor: 'F5F5F5',
            headerFontSize: 12,
            headerBold: true
          });

          currentSlide++;
          currentSlideContent = [[key, value]];
          currentSlideRowCount = rowCount;
        } else {
          currentSlideContent.push([key, value]);
          currentSlideRowCount += rowCount;
        }
      });

      // Add last slide if needed
      if (currentSlideContent.length > 0) {
        const slide = pptx.addSlide();
        slide.background = { color: 'FFFFFF' };

        slide.addText('Overview', {
          x: 0.5,
          y: 0.5,
          w: '90%',
          h: 1,
          fontSize: 24,
          color: '363636',
          bold: true
        });

        slide.addText(`Page ${currentSlide + 1}/${totalSlides}`, {
          x: 0.5,
          y: 1.2,
          w: '90%',
          h: 0.5,
          fontSize: 14,
          color: '666666',
          italic: true
        });

        const tableData = [['Field', 'Value'], ...currentSlideContent];
        slide.addTable(tableData, {
          x: 0.5,
          y: 2,
          w: '90%',
          colW: [2.5, 6],
          border: { type: 'solid', color: 'CCCCCC', pt: 1 },
          fontSize: 11,
          color: '363636',
          align: 'left',
          valign: 'middle',
          headerRow: true,
          headerColor: 'F5F5F5',
          headerFontSize: 12,
          headerBold: true
        });
      }
    }

    // Add USP section
    if (companyData.analysis_data?.usp) {
      const uspEntries = Object.entries(companyData.analysis_data.usp);

      // First, calculate total slides needed
      let totalSlides = 0;
      let currentSlideContent = [];
      let currentSlideRowCount = 0;

      uspEntries.forEach(([key, items]) => {
        if (Array.isArray(items)) {
          const itemsText = items.join('\n');
          const rowCount = estimateRowCount(itemsText);
          const maxRows = getMaxRowsPerSlide(itemsText);

          if (currentSlideRowCount + rowCount > maxRows) {
            totalSlides++;
            currentSlideContent = [[key, items]];
            currentSlideRowCount = rowCount;
          } else {
            currentSlideContent.push([key, items]);
            currentSlideRowCount += rowCount;
          }
        }
      });
      if (currentSlideContent.length > 0) totalSlides++;

      // Now create the slides
      let currentSlide = 0;
      currentSlideContent = [];
      currentSlideRowCount = 0;

      uspEntries.forEach(([key, items]) => {
        if (Array.isArray(items)) {
          const itemsText = items.join('\n');
          const rowCount = estimateRowCount(itemsText);
          const maxRows = getMaxRowsPerSlide(itemsText);

          if (currentSlideRowCount + rowCount > maxRows) {
            // Create new slide with accumulated content
            const slide = pptx.addSlide();
            slide.background = { color: 'FFFFFF' };

            slide.addText('USP & Distinctions', {
              x: 0.5,
              y: 0.5,
              w: '90%',
              h: 1,
              fontSize: 24,
              color: '363636',
              bold: true
            });

            slide.addText(`Page ${currentSlide + 1}/${totalSlides}`, {
              x: 0.5,
              y: 1.2,
              w: '90%',
              h: 0.5,
              fontSize: 14,
              color: '666666',
              italic: true
            });

            const tableData = [['Category', 'Items'], ...currentSlideContent.map(([k, v]) => [k, v.map((item) => `- ${item}`).join('\n')])];
            slide.addTable(tableData, {
              x: 0.5,
              y: 2,
              w: '90%',
              colW: [2.5, 6],
              border: { type: 'solid', color: 'CCCCCC', pt: 1 },
              fontSize: 11,
              color: '363636',
              align: 'left',
              valign: 'middle',
              headerRow: true,
              headerColor: 'F5F5F5',
              headerFontSize: 12,
              headerBold: true
            });

            currentSlide++;
            currentSlideContent = [[key, items]];
            currentSlideRowCount = rowCount;
          } else {
            currentSlideContent.push([key, items]);
            currentSlideRowCount += rowCount;
          }
        }
      });

      // Add last slide if needed
      if (currentSlideContent.length > 0) {
        const slide = pptx.addSlide();
        slide.background = { color: 'FFFFFF' };

        slide.addText('USP & Distinctions', {
          x: 0.5,
          y: 0.5,
          w: '90%',
          h: 1,
          fontSize: 24,
          color: '363636',
          bold: true
        });

        slide.addText(`Page ${currentSlide + 1}/${totalSlides}`, {
          x: 0.5,
          y: 1.2,
          w: '90%',
          h: 0.5,
          fontSize: 14,
          color: '666666',
          italic: true
        });

        const tableData = [['Category', 'Items'], ...currentSlideContent.map(([k, v]) => [k, v.map((item) => `- ${item}`).join('\n')])];
        slide.addTable(tableData, {
          x: 0.5,
          y: 2,
          w: '90%',
          colW: [2.5, 6],
          border: { type: 'solid', color: 'CCCCCC', pt: 1 },
          fontSize: 11,
          color: '363636',
          align: 'left',
          valign: 'middle',
          headerRow: true,
          headerColor: 'F5F5F5',
          headerFontSize: 12,
          headerBold: true
        });
      }
    }

    // Add Market Trends section
    if (companyData.analysis_data?.market_trends) {
      // Add Trends
      if (companyData.analysis_data.market_trends.trends) {
        const trends = companyData.analysis_data.market_trends.trends;
        const totalSlides = Math.ceil(trends.length / 5);

        for (let i = 0; i < trends.length; i += 5) {
          const slide = pptx.addSlide();
          slide.background = { color: 'FFFFFF' };

          slide.addText('Market Trends', {
            x: 0.5,
            y: 0.5,
            w: '90%',
            h: 1,
            fontSize: 24,
            color: '363636',
            bold: true
          });

          slide.addText(`Page ${Math.floor(i / 5) + 1}/${totalSlides}`, {
            x: 0.5,
            y: 1.2,
            w: '90%',
            h: 0.5,
            fontSize: 14,
            color: '666666',
            italic: true
          });

          const tableData = [['Trend']];
          const currentTrends = trends.slice(i, i + 5);
          currentTrends.forEach((trend) => {
            const trendText = typeof trend === 'string' ? trend : `${trend.title || ''}\n${trend.description || ''}`;
            tableData.push([`- ${trendText}`]);
          });

          slide.addTable(tableData, {
            x: 0.5,
            y: 2,
            w: '90%',
            colW: [8.5],
            border: { type: 'solid', color: 'CCCCCC', pt: 1 },
            fontSize: 11,
            color: '363636',
            align: 'left',
            valign: 'middle',
            headerRow: true,
            headerColor: 'F5F5F5',
            headerFontSize: 12,
            headerBold: true
          });
        }
      }

      // Add Benchmarks
      if (companyData.analysis_data.market_trends.benchmarks) {
        const benchmarks = Object.entries(companyData.analysis_data.market_trends.benchmarks);
        const totalSlides = Math.ceil(benchmarks.length / 5);

        for (let i = 0; i < benchmarks.length; i += 5) {
          const slide = pptx.addSlide();
          slide.background = { color: 'FFFFFF' };

          slide.addText('Benchmarks', {
            x: 0.5,
            y: 0.5,
            w: '90%',
            h: 1,
            fontSize: 24,
            color: '363636',
            bold: true
          });

          slide.addText(`Page ${Math.floor(i / 5) + 1}/${totalSlides}`, {
            x: 0.5,
            y: 1.2,
            w: '90%',
            h: 0.5,
            fontSize: 14,
            color: '666666',
            italic: true
          });

          const tableData = [['Benchmark', 'Description']];
          const currentBenchmarks = benchmarks.slice(i, i + 5);
          currentBenchmarks.forEach(([, value]) => {
            const benchmarkText = typeof value === 'string' ? value : `${value.metric_value || ''} ${value.metric_name || ''}`;
            const description = typeof value === 'string' ? '' : value.description || '';
            tableData.push([benchmarkText, description]);
          });

          slide.addTable(tableData, {
            x: 0.5,
            y: 2,
            w: '90%',
            colW: [3, 5.5],
            border: { type: 'solid', color: 'CCCCCC', pt: 1 },
            fontSize: 11,
            color: '363636',
            align: 'left',
            valign: 'middle',
            headerRow: true,
            headerColor: 'F5F5F5',
            headerFontSize: 12,
            headerBold: true
          });
        }
      }
    }

    // Save the presentation
    const fileName = `${companyData.name || removeProtocol(companyData.website) || 'company'}-analysis.pptx`;
    pptx.writeFile({ fileName });
    handleClose();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle case when no data is available
  if (!companyData || !companyData.analysis_data) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          No company data available
        </Typography>
        <Typography>
          We couldn&apos;t find any analysis data for this company. Please make sure you have initiated an analysis for this project.
        </Typography>
      </Paper>
    );
  }

  // Extract the needed data
  const analysisData = companyData.analysis_data;
  const overviewData = analysisData.overview_result?.result?.[0] || null;
  // const businessAnalysis = analysisData.business_analysis || null;
  const marketTrends = analysisData.market_trends || null;
  const uspData = analysisData.usp || null;
  const reviewsData = companyData.reviews_data || null;

  return (
    <Box ref={contentRef}>
      {/* Company Name and Info with Download Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 4,
          gap: 1,
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary'
            }}
          >
            {removeProtocol(companyData.website) || companyData.name || 'Unnamed Company'}
          </Typography>
          {companyData.website && (
            <Tooltip title="Visit Website" arrow>
              <IconButton
                color="primary"
                href={ensureHttpProtocol(companyData.website)}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.lighter'
                  }
                }}
              >
                <ExportOutlined style={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box>
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              height: 28,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
            aria-label="download options"
            className="screenshot-controls"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Menu
            id="download-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            className="screenshot-controls"
            slotProps={{
              paper: {
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1,
                  mt: 1
                }
              }
            }}
          >
            <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
            <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
            <MenuItem onClick={downloadAsPPTX}>Download as PPTX</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Overview Section */}
      {overviewData ? (
        <NewOverviewSection analysisData={overviewData} />
      ) : (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Overview
          </Typography>
          <Typography>No overview data available for this company.</Typography>
        </Paper>
      )}

      {/* Business Analysis Section - Matching ExtendedAnalysisGroup design */}
      {/* <div id="business-analysis">
        <Stack mt={5} spacing={2} direction="row" alignItems="center" justifyContent="center">
          <Typography variant="h3" align="center">
            Business Overview
          </Typography>
          {!businessAnalysis && <CircularProgress size={24} />}
        </Stack>
        {businessAnalysis !== null
          ? Object.entries(businessAnalysis).map(([key, value]) => (
              <React.Fragment key={key}>
                <Typography variant="h4" my={2} align="left">
                  {key}
                </Typography>
                <Typography variant={matchDownSM ? 'body2' : 'body1'}>{value}</Typography>
              </React.Fragment>
            ))
          : null}
      </div> */}

      {/* USP Section - using the reusable component with properly formatted data */}
      {uspData && (
        <USPSection
          usp={uspData}
          isProUser={true} // Always show full data in dashboard
          handleTrialClick={() => {}} // Empty function since trial prompt not needed
        />
      )}

      {/* Reviews Section - simple component that exactly matches original layout */}
      {reviewsData && (
        <Box sx={{ mb: 4 }}>
          <SimpleReviewsSection reviewsData={reviewsData} />
        </Box>
      )}

      {/* Market Trends Section */}
      {marketTrends && <MarketTrends marketTrends={marketTrends} />}
    </Box>
  );
};

export default CompanyAnalysis;
